import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { LimitedTimeOffer } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-limited-time-offer.model';

import { WhatsappTemplateButtonTypeEnum } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplateButton } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-template-button.model';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';

@Component({
  selector: 'app-whatsapp-template-preview',
  templateUrl: './whatsapp-template-preview.component.html',
  styleUrls: ['./whatsapp-template-preview.component.scss'],
})
export class WhatsappTemplatePreviewComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() settingsMode: boolean;
  @Input() templateDisplayedBS: BehaviorSubject<FormGroup> = new BehaviorSubject(null);
  @Input() actualTime!: string;

  templateForm: FormGroup;

  headerText!: string;
  imageSrc!: any;
  videoSrc!: any;
  documentSrc!: boolean;
  mediaMetadata!: {
    filename: string;
    weight: number;
    file: File;
  };
  header: any;
  bodyFormatted: string;
  footerText: string;
  /**
   * The complete list of buttons to display.
   */
  buttons: WhatsappTemplateButton[];
  /**
   * The 2 firsts buttons displayed.
   */
  mainButtons: WhatsappTemplateButton[];
  seeMoreOptionsContainerMarginTop = 0;
  seeMoreOptionsContainerHeight = 0;
  subscriptions: Subscription = new Subscription();
  templateFormChangeSubscription: Subscription = new Subscription();

  WhatsappTemplateButtonTypeEnum = WhatsappTemplateButtonTypeEnum;
  toggleSeeMoreOptions: boolean;
  bodyTruncated = true;
  limitedTimeOffer: LimitedTimeOffer;
  actualTimeLabel: string;
  constructor(
    private whatsappTemplatesService: WhatsappTemplatesService,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef,
    private sitesService: SitesService,
    private dateTimezonePipe: DateTimezonePipe
  ) {}

  ngOnInit(): void {
    this.actualTimeLabel = this.actualTime ? this.dateTimezonePipe.transform(this.actualTime, 'shortTime') : '09:10';
    if (this.settingsMode) {
      this.subscriptions.add(
        this.whatsappTemplatesService.headerMediaFileBS.subscribe(result => {
          this.imageSrc = null;
          this.documentSrc = null;
          this.videoSrc = null;
          this.mediaMetadata = {
            filename: null,
            weight: null,
            file: null,
          };
          if (result) {
            const { file, mediaType } = result;

            this.mediaMetadata = {
              filename: file.name,
              weight: Math.round((file.size / 1000) * 10) / 10,
              file,
            };
            if (mediaType === 'image' || mediaType === 'video') {
              const reader = new FileReader();
              reader.onload = event => {
                const { result } = event.target;
                if (mediaType === 'image') {
                  this.imageSrc = result;
                } else if (mediaType === 'video') {
                  this.videoSrc = this.sanitizer.bypassSecurityTrustResourceUrl(result as any);
                }
              };
              reader.readAsDataURL(file);
            }
          }
        })
      );
    }
    this.subscriptions.add(
      this.templateDisplayedBS.pipe(throttleTime(100)).subscribe(value => {
        if (value) {
          this.templateFormChangeSubscription?.unsubscribe();
          this.templateForm = value;
          this.formatTemplate();
          this.templateFormChangeSubscription.add(
            this.templateForm.valueChanges.subscribe({
              next: () => this.formatTemplate(),
            })
          );
        }
      })
    );
  }

  ngAfterViewChecked(): void {
    if (this.bodyTruncated && this.bodyFormatted && this.elementRef?.nativeElement?.querySelector) {
      const elementRef = this.elementRef.nativeElement;
      const textElement = elementRef.querySelector('.body-formatted');
      if (!textElement) {
        return;
      }
      const isTextTruncated = textElement.scrollHeight > textElement.clientHeight;

      if (isTextTruncated) {
        const showMoreElement = elementRef.querySelector('#show-more-trigger');
        if (!showMoreElement) {
          return;
        }
        showMoreElement['style'].display = 'block';
      }
    }
  }

  seeMoreOptions() {
    const element = document.getElementById('see-more-options-container');
    if (element) {
      this.toggleSeeMoreOptions = !this.toggleSeeMoreOptions;
      const buttonsSize = (this.buttons || []).length;
      const height = this.toggleSeeMoreOptions && buttonsSize ? buttonsSize * 40 + 15 + 67 : 0;

      if (this.settingsMode) {
        this.seeMoreOptionsContainerHeight = height;
      } else {
        const processingPageDelta = this.toggleSeeMoreOptions ? 15 : 0;
        this.seeMoreOptionsContainerHeight = height + processingPageDelta;
      }
      this.seeMoreOptionsContainerMarginTop = -height + 15;
    }
  }

  closeMoreOptions() {
    this.toggleSeeMoreOptions = false;
    this.seeMoreOptionsContainerHeight = 0;
    this.seeMoreOptionsContainerMarginTop = 0;
  }

  private formatTemplate() {
    const { header, limitedTimeOfferEnabled, limited_time_offer, body, footer, buttons, placeholders } =
      this.templateForm.getRawValue();
    this.formatHeader(header);
    if (limitedTimeOfferEnabled) {
      this.formatLimitedTimeOffer(limited_time_offer, buttons);
    } else {
      this.limitedTimeOffer = null;
    }
    this.formatBody(placeholders, body);
    this.footerText = footer;
    this.formatButtons(buttons || []);
  }

  private formatHeader(header) {
    if (header) {
      const { text, type, media_type, media_url } = header;
      this.header = header;
      this.headerText = null;
      this.imageSrc = null;
      this.documentSrc = null;
      this.videoSrc = null;
      this.mediaMetadata = null;
      if (type === 'text') {
        this.headerText = text;
      } else {
        switch (media_type) {
          case 'image':
            this.imageSrc =
              media_url ||
              (this.settingsMode && this.imageSrc) ||
              'assets/whatsapp-cloud-api-examples/alcmeon-logo-112x112.png';
            break;

          case 'document':
            this.documentSrc = true;
            break;

          case 'video':
            this.videoSrc =
              media_url ||
              (this.settingsMode && this.videoSrc) ||
              'assets/whatsapp-cloud-api-examples/whatsapp-teplate-video.mp4';
            break;

          default:
            break;
        }
      }
    }
  }

  private formatLimitedTimeOffer(limitedTimeOffer: LimitedTimeOffer, buttons: WhatsappTemplateButton[]) {
    if (!limitedTimeOffer) {
      return;
    }
    const { text } = limitedTimeOffer;
    const copyCodeButton = (buttons || []).find(({ type }) => type === WhatsappTemplateButtonTypeEnum.COPY_CODE);
    let code: string;
    if (copyCodeButton) {
      code = copyCodeButton.example as string;
    }
    this.limitedTimeOffer = { text, code };
  }

  private formatBody(placeholders, body) {
    const placeholderKeys = Object.keys(placeholders || {});
    if (placeholderKeys?.length && body) {
      this.bodyFormatted = placeholderKeys.reduce(
        (bodyFormatted, placeholder) =>
          bodyFormatted.replace(placeholder, placeholders[placeholder]?.example || placeholders[placeholder]?.label),
        body
      );
    } else {
      this.bodyFormatted = body;
    }

    const regexBold =
      /(?<=(?:^|\s|\b))\*(?!\*{2,})(?!_\*+)(\*?_\S[^*]*?\S_*\*|\S(?:[^*]*?\S)?(?:_\S[^*]*?\S)*)\*(?=(?:\s|$|_|<em>|<\/em>|\S))/g;
    const regexItalic =
      /(?<=(?:^|\s|\b))_(?!_{2,})(?!\*)(?<!_+_\*+)(\*?_\S[^_]*?\S_*\*|\S(?:[^_]*?\S)?(?:\*\S[^_]*?\S)*)_(?=(?:\s|$|\*|<strong>|<\/strong>|\S))/g;

    this.bodyFormatted = (this.bodyFormatted || '')
      .replace(regexBold, (_, p1) => `<strong>${p1}</strong>`)
      .replace(regexItalic, (_, p1) => `<em>${p1}</em>`);
  }

  private formatButtons(buttons: WhatsappTemplateButton[]) {
    buttons.forEach(button => {
      const { url, example, type } = button;
      if (type === WhatsappTemplateButtonTypeEnum.URL) {
        const exampleValue = Array.isArray(example) ? example[0] : example;
        button['href'] = `${(url || '').replace('{{1}}', '')}${exampleValue || ''}`;
      }
    });

    if (this.settingsMode) {
      const siteId = this.whatsappTemplatesService.createTemplateSiteIdUpdatedBS?.value;
      const isWhatsappCloudAPI = this.sitesService.sitesV2Map.get(siteId)?.isCloudAPI;
      const buttonsFiltered = buttons.filter(
        ({ type }) => isWhatsappCloudAPI || type !== WhatsappTemplateButtonTypeEnum.COPY_CODE
      );
      this.buttons = [].concat(buttonsFiltered);
      this.mainButtons = [].concat(buttonsFiltered).splice(0, buttonsFiltered.length > 3 ? 2 : 3);
    } else {
      this.buttons = [].concat(buttons);
      this.mainButtons = [].concat(buttons).splice(0, buttons.length > 3 ? 2 : 3);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.templateFormChangeSubscription?.unsubscribe();
  }
}
